import React from 'react';
import PropTypes from 'prop-types';
import TimeInput from 'react-time-input';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import TextField from '@material-ui/core/TextField';
import get from 'lodash/get';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { borderRadius, colors, fontSize } from '../../constants/styleConstants';
import { removeWithTranslationProps } from '../../utils/common';
import { TP } from '../../constants/consts';
import { useTranslation } from '../../src/i18n';
import { styles } from './FormikAutocompleteSelect';

const useStyles = makeStyles(() => ({
  field: {
    marginTop: 0,
    marginBottom: 0,
    width: '100%',
    maxWidth: 150,
  },
  inputBox: {
    margin: 0,
    border: `1px solid ${colors.fieldsBorderGray} !important`,
    borderRadius: borderRadius.fields,
    backgroundColor: colors.white,
    paddingLeft: 5,
    '&:focus-within': {
      backgroundColor: colors.background.gray,
    },
  },
  inputLabel: {
    fontSize: fontSize.basic,
    color: `${colors.title} !important`,
    letterSpacing: 0.6,
    textAlign: 'left',
    '&.Mui-error': {
      color: `${colors.red} !important`,
    },
  },
  input: {
    padding: '8px 10px !important',
    fontSize: fontSize.textFieldLabel,
    height: 29,
    backgroundColor: colors.white,
    '&:focus': {
      backgroundColor: colors.background.gray,
    },
  },
  underline: {
    '&::after': {
      borderBottom: '0px solid transparent !important',
    },
    '&::before': {
      borderBottom: '0px solid transparent !important',
    },
  },
  disabledField: {
    backgroundColor: `${colors.background.gray} !important`,
    color: `${colors.textGray} !important`,
  },
  shrink: styles.shrink,
}));

const TimePicker = ({
  label,
  className,
  placeholder,
  onTimeChange,
  field,
  form: { errors, touched },
  id,
  errorMessageClass,
  autoFocus,
  ...rest
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const errorText = get(errors, field.name, '')
    .split(' ')
    .map(text => t(text))
    .join(' ');
  const hasError = get(touched, field.name) && Boolean(get(errors, field.name));

  return (
    <TextField
      id={id}
      className={cn(classes.field, className)}
      label={label}
      placeholder={t(placeholder)}
      InputLabelProps={{
        shrink: true,
        classes: {
          root: cn(classes.inputLabel, errorMessageClass),
          shrink: classes.shrink,
        },
      }}
      InputProps={{
        inputComponent: TimeInput,
        startAdornment: <AccessTimeIcon style={{ fill: colors.blue }} />,
        classes: {
          root: classes.inputBox,
          input: classes.input,
          underline: classes.underline,
          disabled: classes.disabledField,
          error: classes.error,
          focused: classes.focusedField,
        },
      }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      inputProps={{
        onTimeChange,
        initTime: field.value,
        mountFocus: autoFocus,
      }}
      FormHelperTextProps={{
        classes: { root: errorMessageClass },
      }}
      error={hasError}
      helperText={errorText}
      {...removeWithTranslationProps(rest)}
      {...field}
    />
  );
};

TimePicker.propTypes = {
  className: PropTypes.string,
  errorMessageClass: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onTimeChange: PropTypes.func.isRequired,
  form: PropTypes.shape({
    errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    touched: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  }),
  field: PropTypes.object,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  autoFocus: PropTypes.bool,
};

TimePicker.defaultProps = {
  form: {
    errors: {},
    touched: {},
  },
  field: {
    name: '',
    value: '',
  },
  placeholder: `${TP}.FN_TIME_PLACEHOLDER`,
  autoFocus: false,
};

export default TimePicker;
